import { Role, type ServerDetail, type TrafficTopRow } from '@bartizo/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { isApiError, needsReauth } from '../api/client.js';
import {
  approveServer,
  getServer,
  listServerGroups,
  removeServer,
  updateServer,
} from '../api/servers.js';
import { listEvents } from '../api/events.js';
import { getTrafficSeries, getTrafficTop } from '../api/traffic.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ConfigHistoryCard } from '../components/ConfigHistoryCard.js';
import { CrowdsecCard } from '../components/CrowdsecCard.js';
import { CrowdsecCollectionsCard } from '../components/CrowdsecCollectionsCard.js';
import { CrowdsecHealthCard } from '../components/CrowdsecHealthCard.js';
import { DatabaseCard } from '../components/DatabaseCard.js';
import { StatIcon } from '../ui/StatIcon.js';
import { ActivityIcon, AlertIcon, CpuIcon, ShieldIcon } from '../ui/icons.js';
import { DiskCard } from '../components/DiskCard.js';
import { SystemCard } from '../components/SystemCard.js';
import { HardeningTab } from '../components/HardeningTab.js';
import { InventoryTab } from '../components/InventoryTab.js';
import { VulnerabilitiesTab } from '../components/VulnerabilitiesTab.js';
import { PageHeader } from '../components/PageHeader.js';
import { ServerEventFilters } from '../components/ServerEventFilters.js';
import { ServerSettingsCard } from '../components/ServerSettingsCard.js';
import { RiskBadge, ServerStatusBadge } from '../components/StatusBadge.js';
import { WazuhAgentCard } from '../components/WazuhAgentCard.js';
import { WebEnvironmentCard } from '../components/WebEnvironmentCard.js';
import {
  anyEventFilter,
  NO_EVENT_FILTERS,
  toEventFilters,
  type EventFilterState,
} from '../lib/event-filters.js';
import { formatPercent } from '../lib/format.js';
import { trafficRisk } from '../lib/traffic-risk.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, Facts, ShareBar, type Column } from '../ui/DataTable.js';
import { SelectField } from '../ui/Field.js';
import { TabPanel, Tabs, type TabItem } from '../ui/Tabs.js';
import { useTab } from '../ui/use-tab.js';
import { eventColumns } from '../components/event-columns.js';
import { KeyCell } from '../components/traffic-parts.js';

/**
 * One server (knowledge doc sections 7, 10, 12 and 15), in six tabs (`?tab=`):
 * Overview, Events, Metrics, Hardening (P11.8.6), Inventory (P11.8.7), Vulnerabilities (P11.8.8), Configuration and Actions. Requests are the Traffic
 * page's, scoped to this server, rather than a tab of their own.
 *
 * Three actions, each at the level section 12 puts it: an admin approves a pending
 * server, an admin changes its roles and group, and removing it — High risk — asks
 * for the credential first and then for the server's own name.
 */

const NO_GROUP = '__none__';

export function ServerDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const session = useCurrentUser();
  const isAdmin = session.user.role === 'admin';
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);

  const queryClient = useQueryClient();
  const server = useQuery({ queryKey: ['server', id], queryFn: () => getServer(id) });
  const [tab, setTab] = useTab(TABS);

  if (server.isPending) {
    return (
      <div className="page">
        <div className="skeleton skeleton-title" aria-hidden="true" />
        <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading the server" />
      </div>
    );
  }

  if (server.isError) {
    return (
      <div className="page">
        <PageHeader title="Server" />
        <Alert tone="danger" title="Could not load that server">
          {isApiError(server.error) && server.error.status === 404
            ? 'It may have been removed.'
            : 'Try again in a moment.'}{' '}
          <Link to="/servers">Back to the fleet</Link>.
        </Alert>
      </div>
    );
  }

  const detail = server.data;
  const refresh = (updated: ServerDetail): void => {
    queryClient.setQueryData(['server', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['servers'] });
  };
  const live = detail.status !== 'revoked';
  const serverQuery = `server_id=${encodeURIComponent(detail.id)}`;

  return (
    <div className="page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/servers">Servers</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{detail.name}</span>
      </nav>
      <PageHeader
        title={detail.name}
        description={
          <span className="header-facts">
            <ServerStatusBadge status={detail.status} />
            <span>
              {detail.lastHeartbeat === null
                ? 'No heartbeat yet'
                : `Last heartbeat ${formatRelative(detail.lastHeartbeat)}`}
            </span>
            {detail.group !== null && <span>Group {detail.group.name}</span>}
            {detail.roles.length > 0 && <span>{detail.roles.join(', ')}</span>}
          </span>
        }
        actions={
          <>
            <Link className="button button-secondary" to={`/events?${serverQuery}`}>
              View events
            </Link>
            {detail.roles.includes('web') && (
              <Link className="button button-secondary" to={`/traffic?${serverQuery}`}>
                View requests
              </Link>
            )}
          </>
        }
      />

      {detail.status === 'pending' && (
        <PendingPanel detail={detail} isAdmin={isAdmin} onApproved={refresh} />
      )}

      {detail.status === 'revoked' && (
        <Alert tone="warning" title="This server has been removed">
          Its key no longer authenticates, so it can send nothing. The row is kept so its events and
          metrics still have a server to belong to.
        </Alert>
      )}

      {live && detail.capabilities?.restart !== undefined && (
        <Alert tone="warning" title="This server needs a restart">
          {detail.capabilities.restart.since === undefined
            ? 'Something installed or fixed here only takes effect after a reboot.'
            : `Something installed or fixed here has waited for a reboot since ${formatRelative(detail.capabilities.restart.since)}.`}{' '}
          {detail.capabilities.restart.packages.length > 0 &&
            `Asked for by ${detail.capabilities.restart.packages.map((name) => (name === 'bartizo-audit' ? 'Bartizo’s audit settings (kernel boot options)' : name)).join(', ')}. `}
          The compliance checks that depend on it are judged again after the restart, and this
          notice goes by itself.
        </Alert>
      )}

      <Tabs
        label="About this server"
        tabs={TABS.map((each) =>
          each.id === 'events' ? { ...each, count: detail.attention?.openHigh } : each,
        )}
        current={tab}
        onSelect={setTab}
        idPrefix="server"
      />

      <TabPanel idPrefix="server" current={tab}>
        {tab === 'overview' && (
          <OverviewTab detail={detail} timezone={timezone} isAdmin={isAdmin} />
        )}

        {tab === 'events' && <EventsTab detail={detail} timezone={timezone} />}

        {tab === 'metrics' && (
          <>
            {/* Section 8.3's disk history and folder sizes. Hidden for a revoked
                server: its agent stopped reporting, so a chart would only show the
                past trailing off, which reads as a fault rather than a decision. */}
            {live && <DiskCard serverId={detail.id} timezone={timezone} />}
            {/* Section 8.4. Draws itself only once the server has reported a database. */}
            {live && <DatabaseCard serverId={detail.id} timezone={timezone} />}
            {live && <SystemCard serverId={detail.id} timezone={timezone} />}
          </>
        )}

        {tab === 'hardening' && <HardeningTab serverId={detail.id} timezone={timezone} />}

        {tab === 'inventory' && <InventoryTab serverId={detail.id} timezone={timezone} />}

        {tab === 'vulnerabilities' && (
          <VulnerabilitiesTab serverId={detail.id} timezone={timezone} />
        )}

        {tab === 'configuration' && (
          <>
            {/* P11.9.1: whether CrowdSec reads its logs and something enforces its bans. */}
            {live && (
              <CrowdsecHealthCard
                serverId={detail.id}
                installed={detail.capabilities?.crowdsec?.installed}
                timezone={timezone}
              />
            )}
            {/* P11.9.2: what CrowdSec detects here, what only watches, what would suit. */}
            {live && (
              <CrowdsecCollectionsCard
                serverId={detail.id}
                roles={detail.roles}
                installed={detail.capabilities?.crowdsec?.installed}
                latestVersion={detail.configStatus?.latestVersion ?? null}
              />
            )}
            {/* Section 8.8: what is in front of the websites, and an admin's confirmation. */}
            {live && (
              <WebEnvironmentCard
                serverId={detail.id}
                hasWebRole={detail.roles.includes('web')}
                configStatus={detail.configStatus}
                health={detail.moduleHealth?.['web_env']}
                timezone={timezone}
              />
            )}
            {isAdmin && live && <RolesAndGroupCard detail={detail} onSaved={refresh} />}
            {isAdmin && live && (
              <>
                <ServerSettingsCard
                  serverId={detail.id}
                  latestVersion={detail.configStatus?.latestVersion ?? null}
                  detectedDatabases={detail.capabilities?.databases}
                />
                <ConfigHistoryCard serverId={detail.id} timezone={timezone} />
              </>
            )}
            {Object.keys(detail.labels).length > 0 && (
              <Card title="Labels" description="Applied by the enrollment token this server used.">
                <dl className="details">
                  {Object.entries(detail.labels).map(([key, value]) => (
                    <div key={key} style={{ display: 'contents' }}>
                      <dt>{key}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
            )}
            {!isAdmin && (
              <p className="muted text-sm">
                Roles, group and settings are an administrator&apos;s to change.
              </p>
            )}
          </>
        )}

        {tab === 'actions' && (
          <>
            {/* Section 8.1 and 12: what CrowdSec blocks here, and banning from the console. */}
            {live && (
              <CrowdsecCard
                server={{ id: detail.id, name: detail.name, group: detail.group }}
                installed={detail.capabilities?.crowdsec?.installed}
                timezone={timezone}
              />
            )}
            <Card
              title="Actions taken here"
              description="Bans, unbans and changes sent to this server, and what each one did."
            >
              <Link to={`/actions?${serverQuery}`}>See this server&apos;s actions →</Link>
            </Card>
            {isAdmin && live && <RemoveCard detail={detail} />}
          </>
        )}
      </TabPanel>
    </div>
  );
}

type ServerTab =
  | 'overview'
  | 'events'
  | 'metrics'
  | 'hardening'
  | 'inventory'
  | 'vulnerabilities'
  | 'configuration'
  | 'actions';

const TABS: readonly TabItem<ServerTab>[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'events', label: 'Events' },
  { id: 'metrics', label: 'Metrics' },
  // P11.8.6: what Wazuh's assessment and rootcheck found, apart from Bartizo's own settings.
  { id: 'hardening', label: 'Hardening' },
  // P11.8.7: what is installed, listening and running, from Wazuh's syscollector.
  { id: 'inventory', label: 'Inventory' },
  // P11.8.8: the installed packages with known vulnerabilities, and the fix.
  { id: 'vulnerabilities', label: 'Vulnerabilities' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'actions', label: 'Actions' },
];

const DAY_MS = 24 * 3_600_000;
const count = (value: number): string => Math.round(value).toLocaleString('en-US');

/**
 * What the server is and how it is doing, on one screen: its facts and modules,
 * the last day's security numbers, its latest events, and — for a web server —
 * what is asked of it most and by whom, each list opening the full explorer.
 */
function OverviewTab({
  detail,
  timezone,
  isAdmin,
}: {
  detail: ServerDetail;
  timezone: string;
  isAdmin: boolean;
}): ReactNode {
  const from = useMemo(() => new Date(Date.now() - DAY_MS).toISOString(), []);
  const isWeb = detail.roles.includes('web');
  const events = useQuery({
    queryKey: ['events', 'server-overview', detail.id],
    queryFn: () => listEvents({ server_id: detail.id, from, limit: 100 }),
  });
  const traffic = useQuery({
    queryKey: ['traffic', 'server-overview', detail.id],
    queryFn: () => getTrafficSeries({ serverId: detail.id, from }),
    enabled: isWeb,
  });

  const dayEvents = events.data?.items ?? [];
  const more = events.data?.nextCursor !== null && events.data?.nextCursor !== undefined;
  const high = dayEvents.filter((each) => each.severity === 'high' || each.severity === 'critical');
  const requests = (traffic.data?.points ?? []).reduce(
    (sum, point) => ({
      all: sum.all + point.requests,
      refused: sum.refused + point.status4xx,
      errors: sum.errors + point.status5xx,
    }),
    { all: 0, refused: 0, errors: 0 },
  );
  const serverQuery = `server_id=${encodeURIComponent(detail.id)}`;

  return (
    <>
      <dl className="stat-tiles">
        <div className={`stat-tile ${high.length > 0 ? 'stat-tile-alert' : ''}`}>
          <StatIcon icon={AlertIcon} tone={high.length > 0 ? 'danger' : 'ok'} />
          <dt>High or critical, 24 h</dt>
          <dd>{events.isPending ? '…' : `${count(high.length)}${more ? '+' : ''}`}</dd>
          <p>{detail.attention?.openHigh ?? 0} still open</p>
          <Link to={`/events?${serverQuery}&min_severity=high`} className="stat-link">
            View all →
          </Link>
        </div>
        <div className="stat-tile">
          <StatIcon icon={ShieldIcon} />
          <dt>Events, 24 h</dt>
          <dd>{events.isPending ? '…' : `${count(dayEvents.length)}${more ? '+' : ''}`}</dd>
          <p>Every category</p>
          <Link to={`/events?${serverQuery}`} className="stat-link">
            View all →
          </Link>
        </div>
        {isWeb && (
          <div className="stat-tile">
            <StatIcon icon={ActivityIcon} />
            <dt>Requests, 24 h</dt>
            <dd>{traffic.isPending ? '…' : count(requests.all)}</dd>
            <p>
              {requests.all === 0
                ? 'None recorded'
                : `${formatPercent((requests.refused / requests.all) * 100, 0)} refused · ${formatPercent((requests.errors / requests.all) * 100, 0)} server errors`}
            </p>
            <Link to={`/traffic?${serverQuery}`} className="stat-link">
              View all →
            </Link>
          </div>
        )}
        <div
          className={`stat-tile ${(detail.attention?.unhealthy.length ?? 0) > 0 ? 'stat-tile-alert' : ''}`}
        >
          <StatIcon
            icon={CpuIcon}
            tone={(detail.attention?.unhealthy.length ?? 0) > 0 ? 'danger' : 'ok'}
          />
          <dt>Modules</dt>
          <dd>
            {Object.values(detail.moduleHealth ?? {}).filter((each) => each.ok).length}
            <span className="stat-of">
              {' '}
              of {Object.keys(detail.moduleHealth ?? {}).length} healthy
            </span>
          </dd>
          <p>
            {detail.attention?.settingsFailed === true
              ? 'Its settings did not apply'
              : (detail.attention?.unhealthy.length ?? 0) > 0
                ? `Failing: ${detail.attention?.unhealthy.join(', ') ?? ''}`
                : 'As of its last heartbeat'}
          </p>
        </div>
      </dl>

      <div className="split-grid">
        <Card title="Health and system">
          <Facts
            items={[
              ['Hostname', detail.hostname],
              ['Address', detail.ip ?? <span className="muted">unknown</span>],
              ['Operating system', detail.os ?? <span className="muted">unknown</span>],
              ['Agent version', detail.agentVersion ?? <span className="muted">unknown</span>],
              [
                'Last heartbeat',
                detail.lastHeartbeat === null ? (
                  <span className="muted">never</span>
                ) : (
                  <span title={formatInstant(detail.lastHeartbeat, { timezone })}>
                    {formatRelative(detail.lastHeartbeat)}
                  </span>
                ),
              ],
              ['Enrolled', formatInstant(detail.createdAt, { timezone })],
              ['Settings applied', `version ${String(detail.appliedConfigVersion)}`],
              [
                'Server id',
                <>
                  <code>{detail.id}</code>
                  {isAdmin && (
                    <span className="cell-sub">
                      <Link to={`/audit-log?target=${encodeURIComponent(detail.id)}`}>
                        Everything done to this server
                      </Link>
                    </span>
                  )}
                </>,
              ],
            ]}
          />
          {detail.ip !== null && (
            <p className="muted text-sm">
              The address is self-reported at enrollment; nothing decides on it.
            </p>
          )}
        </Card>
        <ModuleHealthCard detail={detail} />
      </div>
      {/* P11.8.5: the Wazuh agent as the central manager sees it, not as the server says. */}
      {detail.wazuhAgent !== null && (
        <WazuhAgentCard agent={detail.wazuhAgent} timezone={timezone} />
      )}

      <Card
        title="What happened here lately"
        aside={
          <Link to={`/events?${serverQuery}`} className="text-sm">
            View all →
          </Link>
        }
      >
        {events.isError && <p className="muted text-sm">Could not load its events.</p>}
        {events.isPending && <div className="skeleton skeleton-block" aria-hidden="true" />}
        {events.data !== undefined && (
          <DataTable
            caption="This server's latest events"
            columns={eventColumns(timezone, { withServer: false })}
            rows={dayEvents.slice(0, 8)}
            rowKey={(event) => event.id}
            empty={<p className="muted text-sm">No events in the last day.</p>}
          />
        )}
      </Card>

      {isWeb && (
        <div className="split-grid">
          <ServerTopCard
            title="Most requested pages"
            serverId={detail.id}
            from={from}
            dimension="path"
            noun="Page"
          />
          <ServerTopCard
            title="Who asks the most"
            serverId={detail.id}
            from={from}
            dimension="ip"
            noun="Address"
          />
        </div>
      )}
    </>
  );
}

/** One of a web server's top lists for the last day, five rows, opening the explorer. */
function ServerTopCard({
  title,
  serverId,
  from,
  dimension,
  noun,
}: {
  title: string;
  serverId: string;
  from: string;
  dimension: 'path' | 'ip';
  noun: string;
}): ReactNode {
  const top = useQuery({
    queryKey: ['traffic-top', 'server-overview', serverId, dimension],
    queryFn: () => getTrafficTop({ serverId, from }, dimension, 5),
  });
  const rows = top.data?.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.requests, 0);
  const columns: Column<TrafficTopRow>[] = [
    {
      key: 'key',
      header: noun,
      rowHeader: true,
      cell: (row) => <KeyCell dimension={dimension} row={row} status={undefined} />,
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'end',
      cell: (row) => <ShareBar value={row.requests} total={total} />,
    },
    {
      key: 'risk',
      header: 'Risk',
      cell: (row) => {
        const reading = trafficRisk(row);
        return <RiskBadge risk={reading.risk} why={reading.why} />;
      },
    },
  ];

  return (
    <Card
      title={title}
      aside={
        <Link
          to={`/traffic/${dimension}?server_id=${encodeURIComponent(serverId)}&hours=24`}
          className="text-sm"
        >
          View all →
        </Link>
      }
    >
      {top.isError && <p className="muted text-sm">Could not load this list.</p>}
      {top.isPending && <div className="skeleton skeleton-block" aria-hidden="true" />}
      {top.data !== undefined && (
        <DataTable
          caption={title}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          empty={<p className="muted text-sm">No requests in the last day.</p>}
        />
      )}
    </Card>
  );
}

/** Every event here, newest first, a page at a time, with the Events page's filters. */
function EventsTab({ detail, timezone }: { detail: ServerDetail; timezone: string }): ReactNode {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [previous, setPrevious] = useState<(string | undefined)[]>([]);
  const [filter, setFilter] = useState<EventFilterState>(NO_EVENT_FILTERS);
  // Pinned when the range changes, not recomputed each render: see `EventsPage`.
  const from = useMemo(
    () =>
      filter.hours === 'all'
        ? undefined
        : new Date(Date.now() - Number(filter.hours) * 3_600_000).toISOString(),
    [filter.hours],
  );
  const filters = toEventFilters(filter, from);
  const events = useQuery({
    queryKey: ['events', 'server-tab', detail.id, filters, cursor],
    queryFn: () =>
      listEvents({
        server_id: detail.id,
        limit: 25,
        ...filters,
        ...(cursor === undefined ? {} : { cursor }),
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <Card
      title="Events"
      description="Newest first. Filter by severity, category, ATT&CK, state or time."
      aside={
        <Link to={`/events?server_id=${encodeURIComponent(detail.id)}`} className="text-sm">
          Open in Events →
        </Link>
      }
    >
      <ServerEventFilters
        value={filter}
        onChange={(next) => {
          // Another filter, another result: the old cursors belong to the last one.
          setFilter(next);
          setCursor(undefined);
          setPrevious([]);
        }}
      />
      {events.isError && <p className="muted text-sm">Could not load its events.</p>}
      {events.isPending && <div className="skeleton skeleton-block" aria-hidden="true" />}
      {events.data !== undefined && (
        <>
          <DataTable
            caption="This server's events"
            columns={eventColumns(timezone, { withServer: false })}
            rows={events.data.items}
            rowKey={(event) => event.id}
            empty={
              <p className="muted text-sm">
                {anyEventFilter(filter)
                  ? 'No event of this server matches. Widen the filters, or look further back.'
                  : 'No events from this server yet.'}
              </p>
            }
          />
          <div className="row">
            <Button
              disabled={previous.length === 0}
              onClick={() => {
                setCursor(previous[previous.length - 1]);
                setPrevious((list) => list.slice(0, -1));
              }}
            >
              Newer
            </Button>
            <Button
              disabled={events.data.nextCursor === null}
              onClick={() => {
                setPrevious((list) => [...list, cursor]);
                setCursor(events.data.nextCursor ?? undefined);
              }}
            >
              Older
            </Button>
            <span className="muted text-sm">
              Page {previous.length + 1}
              {events.isFetching ? ' · loading…' : ''}
            </span>
          </div>
        </>
      )}
    </Card>
  );
}

function ModuleHealthCard({ detail }: { detail: ServerDetail }): ReactNode {
  const modules = Object.entries(detail.moduleHealth ?? {});

  return (
    <Card
      title="Modules"
      description="What the agent reported in its last heartbeat."
      aside={
        modules.length > 0 && modules.every(([, health]) => health.ok) ? (
          <span className="badge badge-success">All healthy</span>
        ) : undefined
      }
    >
      {modules.length === 0 ? (
        <p className="muted text-sm">
          Nothing yet. A server reports its modules with every heartbeat, about once a minute.
        </p>
      ) : (
        <dl className="details">
          {modules.map(([name, health]) => (
            <div key={name} style={{ display: 'contents' }}>
              <dt>{name}</dt>
              <dd>
                <span className={health.ok ? 'badge badge-success' : 'badge badge-danger'}>
                  {health.ok ? 'ok' : 'failing'}
                </span>
                {health.detail !== undefined && <span className="cell-sub">{health.detail}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

function PendingPanel({
  detail,
  isAdmin,
  onApproved,
}: {
  detail: ServerDetail;
  isAdmin: boolean;
  onApproved: (server: ServerDetail) => void;
}): ReactNode {
  const approve = useMutation({
    mutationFn: () => approveServer(detail.id),
    onSuccess: onApproved,
  });

  return (
    <Card
      title="Waiting for approval"
      description="It enrolled with a token that asks an administrator to look first."
    >
      <p className="text-sm">
        {detail.lastHeartbeat === null ? (
          <>
            It has <strong>not sent a heartbeat yet</strong>, so the agent may not be running. Check
            the server before approving it.
          </>
        ) : (
          <>
            Its agent is running — the last heartbeat was {formatRelative(detail.lastHeartbeat)}.
            Until it is approved it can send heartbeats and nothing else.
          </>
        )}
      </p>
      {approve.isError && (
        <Alert tone="danger">
          {approve.error instanceof Error ? approve.error.message : 'Could not approve it.'}
        </Alert>
      )}
      {isAdmin ? (
        <div className="row">
          <Button
            variant="primary"
            busy={approve.isPending}
            onClick={() => {
              approve.mutate();
            }}
          >
            Approve this server
          </Button>
        </div>
      ) : (
        <p className="muted text-sm">Only an administrator can approve a server.</p>
      )}
    </Card>
  );
}

function RolesAndGroupCard({
  detail,
  onSaved,
}: {
  detail: ServerDetail;
  onSaved: (server: ServerDetail) => void;
}): ReactNode {
  const [roles, setRoles] = useState<string[]>(detail.roles);
  const [groupId, setGroupId] = useState<string>(detail.group?.id ?? NO_GROUP);
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  const save = useMutation({
    mutationFn: () =>
      updateServer(detail.id, {
        roles: roles.map((role) => Role.parse(role)),
        groupId: groupId === NO_GROUP ? null : groupId,
      }),
    onSuccess: onSaved,
  });

  const changed =
    roles.join(',') !== detail.roles.join(',') || groupId !== (detail.group?.id ?? NO_GROUP);

  return (
    <Card
      title="Roles and group"
      description="Section 7: each role adds the modules and settings that role needs."
    >
      <fieldset className="fieldset">
        <legend>Roles</legend>
        {Role.options.map((role) => (
          <label key={role} className="checkbox">
            <input
              type="checkbox"
              checked={roles.includes(role)}
              onChange={(event) => {
                setRoles((current) =>
                  event.target.checked
                    ? [...current, role]
                    : current.filter((each) => each !== role),
                );
              }}
            />
            {role}
          </label>
        ))}
      </fieldset>

      <SelectField
        label="Group"
        hint="A group's settings apply to every server in it, from P3.1 onwards."
        value={groupId}
        onChange={(event) => {
          setGroupId(event.target.value);
        }}
      >
        <option value={NO_GROUP}>No group</option>
        {(groups.data ?? []).map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </SelectField>

      {save.isError && (
        <Alert tone="danger">
          {save.error instanceof Error ? save.error.message : 'Could not save that.'}
        </Alert>
      )}
      {save.isSuccess && !changed && <Alert tone="success">Saved.</Alert>}

      <div className="row">
        <Button
          variant="primary"
          disabled={!changed}
          busy={save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          Save changes
        </Button>
      </div>
    </Card>
  );
}

/** Section 12, High risk: admin, a credential re-check, and then the server's name. */
function RemoveCard({ detail }: { detail: ServerDetail }): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ensureReauthenticated } = useReauth();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => removeServer(detail.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['servers'] });
      void queryClient.invalidateQueries({ queryKey: ['server', detail.id] });
      setConfirming(false);
      void navigate('/servers');
    },
    onError: (caught: unknown) => {
      setError(
        needsReauth(caught)
          ? 'That needed a fresh confirmation. Try again.'
          : caught instanceof Error
            ? caught.message
            : 'Could not remove it.',
      );
    },
  });

  const start = async (): Promise<void> => {
    setError(null);
    // Asked before the action rather than after the API refuses it, so a
    // destructive button never appears to fail and then work on the second press.
    if (await ensureReauthenticated()) setConfirming(true);
  };

  return (
    <Card title="Remove this server" description="This cannot be undone from the console.">
      <Alert tone="warning">
        Removing it drops its key, so its agent stops being able to send anything within seconds.
        Its events and metrics are kept. To bring the machine back you would run the installer again
        with a new enrollment token.
      </Alert>
      <div className="row">
        <Button variant="danger" onClick={() => void start()}>
          Remove server
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Remove this server?"
          description="Its key is dropped immediately and its agent stops reporting."
          confirmLabel="Remove it"
          confirmPhrase={detail.id}
          danger
          busy={remove.isPending}
          error={error}
          onConfirm={() => {
            remove.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      )}
    </Card>
  );
}
